import { useCallback, useEffect, useState } from "react";
import { Show, SignIn, useClerk } from "@clerk/react";
import { toast } from "sonner";
import { KeyRound, Copy, Trash2, AlertTriangle, Code2, ShieldCheck, Lock, ShieldX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi, errMsg, money, fmtDate, timeAgo, BACKEND_URL } from "@/lib/api";
import { useMe } from "@/lib/me";
import { ConsoleCtx, useConsoleApi } from "@/lib/console";
import { EmptyState, Spinner, StatusBadge } from "@/components/shared/common";
import { DTitle, DataTable } from "@/pages/dashboard/Layout";
import { PaymentLinks } from "@/pages/PaymentLinks";

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
const STORE = "dm-console";

export function useConsoleGet(url) {
  const api = useConsoleApi();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(url).then((r) => setD(r.data)).catch(() => setD([])), [api, url]);
  useEffect(() => { load(); }, [load]);
  return [d, load];
}

const Code = ({ children, testId }) => (
  <div className="relative">
    <pre className="overflow-x-auto rounded-xl bg-[#0F172A] p-5 font-mono text-xs leading-relaxed text-slate-200" data-testid={testId}>{children}</pre>
    <button onClick={() => copy(children)} className="absolute right-3 top-3 rounded-md bg-white/10 p-1.5 text-slate-200 hover:bg-white/20" aria-label="Copy"><Copy className="h-3.5 w-3.5" /></button>
  </div>
);

function Keys() {
  const api = useConsoleApi();
  const [keys, load] = useConsoleGet("/developer/keys");
  const [name, setName] = useState("");
  const [secret, setSecret] = useState(null);
  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/developer/keys", { name }); setSecret(data.secret); setName(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Apps using it will stop working immediately.")) return;
    try { await api.delete(`/developer/keys/${id}`); toast.success("Key revoked"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={create} className="card-surface flex flex-wrap items-center gap-3 p-5" data-testid="api-key-create-form">
        <KeyRound className="h-5 w-5 text-primary" />
        <Input required minLength={2} className="max-w-xs" placeholder="Key name (e.g. My SaaS backend)" value={name} onChange={(e) => setName(e.target.value)} data-testid="api-key-name-input" />
        <Button type="submit" data-testid="api-key-generate-button">Generate API key</Button>
      </form>
      {secret && (
        <div className="card-surface space-y-3 p-5" data-testid="api-key-secret-box">
          <p className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300"><AlertTriangle className="h-4 w-4" />Copy this key now — it will never be shown again.</p>
          <div className="flex gap-2"><Input readOnly value={secret} className="font-mono text-xs" data-testid="api-key-secret-value" /><Button variant="outline" onClick={() => copy(secret)} data-testid="api-key-copy-button"><Copy className="h-4 w-4" /></Button></div>
          <Button variant="ghost" size="sm" onClick={() => setSecret(null)} data-testid="api-key-dismiss">I've saved it</Button>
        </div>
      )}
      <DataTable rows={keys} empty="No API keys yet." testId="api-keys-table" cols={[
        { h: "Name", r: (k) => k.name }, { h: "Key", r: (k) => <span className="font-mono text-xs">{k.prefix}…{k.last4}</span> },
        { h: "Mode", r: (k) => <span className="text-xs font-semibold uppercase">{k.mode}</span> },
        { h: "Status", r: (k) => <StatusBadge status={k.revoked ? "revoked" : "active"} testId={`api-key-status-${k.id}`} /> },
        { h: "Last used", r: (k) => (k.last_used_at ? timeAgo(k.last_used_at) : "Never") }, { h: "Created", r: (k) => fmtDate(k.created_at) },
        { h: "", r: (k) => !k.revoked && <Button size="sm" variant="ghost" onClick={() => revoke(k.id)} data-testid={`api-key-revoke-${k.id}`}><Trash2 className="h-4 w-4" /></Button> },
      ]} />
    </div>
  );
}

function Docs() {
  const [docs] = useConsoleGet("/console/docs");
  const base = `${BACKEND_URL}/api/v1`;
  if (!docs) return <Spinner />;
  return (
    <div className="space-y-8 text-sm" data-testid="console-docs">
      {docs.map((s, i) => (
        <div key={i} className="space-y-2"><h3 className="font-semibold">{s.title}</h3>{s.text && <p className="text-muted-foreground">{s.text}</p>}
          {s.code && <Code testId={`api-doc-${i}`}>{s.code.replaceAll("{BASE}", base)}</Code>}</div>))}
    </div>
  );
}

function ApiPayments() {
  const [rows] = useConsoleGet("/developer/payments");
  return <DataTable rows={rows} empty="No API payments yet." testId="api-payments-table" cols={[
    { h: "Number", r: (o) => <span className="font-mono">{o.number}</span> }, { h: "Description", r: (o) => o.title }, { h: "Reference", r: (o) => o.reference || "—" },
    { h: "Status", r: (o) => <StatusBadge status={o.paid ? "completed" : o.status} /> }, { h: "Amount", r: (o) => money(o.total_cents) }, { h: "Net", r: (o) => money(o.vendor_earnings_cents) },
    { h: "Created", r: (o) => fmtDate(o.created_at) }]} />;
}

const Shell = ({ children }) => (
  <div className="container-x flex min-h-[75vh] items-center justify-center py-16">
    <div className="w-full max-w-md space-y-6">
      <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-5 w-5 text-primary" />DevMarket Developer Console</div>
      {children}
    </div>
  </div>
);

function Unlock({ enabled, onUnlocked }) {
  const api = useApi();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!enabled) api.post("/console/totp/setup").then((r) => setSetup(r.data)).catch((e) => toast.error(errMsg(e))); }, [enabled, api]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/console/unlock", { code }); onUnlocked(data); } catch (err) { toast.error(errMsg(err)); setCode(""); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card-surface space-y-5 p-8" data-testid="console-unlock-form">
      {!enabled ? (
        <>
          <div><h1 className="text-xl font-bold">Set up 2-step verification</h1><p className="mt-1 text-sm text-muted-foreground">Scan with Google Authenticator, Authy or 1Password, then enter the 6-digit code.</p></div>
          {setup ? <><img src={setup.qr} alt="2FA QR code" className="mx-auto h-48 w-48 rounded-lg bg-white p-2" data-testid="console-totp-qr" />
            <p className="break-all text-center font-mono text-xs text-muted-foreground" data-testid="console-totp-secret">{setup.secret}</p></> : <Spinner />}
        </>
      ) : <div><h1 className="text-xl font-bold">Enter verification code</h1><p className="mt-1 text-sm text-muted-foreground">Open your authenticator app and enter the 6-digit code.</p></div>}
      <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" required placeholder="123456" value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="text-center font-mono text-2xl tracking-[0.5em]" data-testid="console-code-input" />
      <Button type="submit" className="w-full" disabled={busy || code.length !== 6} data-testid="console-unlock-button"><Lock className="mr-2 h-4 w-4" />{enabled ? "Unlock console" : "Verify & unlock"}</Button>
    </form>
  );
}

function Gate() {
  const api = useApi();
  const { me, loading } = useMe();
  const { signOut } = useClerk();
  const [status, setStatus] = useState(null);
  const [sess, setSess] = useState(() => { try { const s = JSON.parse(sessionStorage.getItem(STORE)); return s && s.expires_at > new Date().toISOString() ? s : null; } catch { return null; } });
  useEffect(() => { if (me) api.get("/console/status").then((r) => setStatus(r.data)).catch(() => setStatus({ denied: true })); }, [me, api]);
  const lock = useCallback(() => { sessionStorage.removeItem(STORE); setSess(null); }, []);
  useEffect(() => {
    if (!sess) return;
    const t = setTimeout(() => { lock(); toast.info("Console locked after 30 minutes"); }, new Date(sess.expires_at) - Date.now());
    return () => clearTimeout(t);
  }, [sess, lock]);
  if (loading || !me || !status) return <Shell><Spinner /></Shell>;
  if (status.denied) return <Shell><div className="card-surface space-y-3 p-8" data-testid="console-access-denied"><ShieldX className="h-6 w-6 text-rose-500" /><h1 className="text-xl font-bold">Access denied</h1>
    <p className="text-sm text-muted-foreground">This account isn't authorized for the developer console.</p><Button variant="outline" onClick={() => signOut()} data-testid="console-signout">Sign out</Button></div></Shell>;
  if (!sess) return <Shell><Unlock enabled={status.totp_enabled} onUnlocked={(d) => { sessionStorage.setItem(STORE, JSON.stringify(d)); setSess(d); setStatus({ ...status, totp_enabled: true }); }} /></Shell>;
  const doLock = () => { api.post("/console/lock", null, { headers: { "X-Console-Token": sess.token } }).finally(lock); };
  return (
    <ConsoleCtx.Provider value={{ token: sess.token, onExpired: lock }}>
      <div className="container-x py-10" data-testid="developer-control-page">
        <DTitle title="Developer console" text={`Accept payments on any website. Signed in as ${status.email}. Session expires ${timeAgo(sess.expires_at).replace(" ago", "")}.`}>
          <span className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground"><Code2 className="h-3.5 w-3.5" />REST API v1</span>
          <Button variant="outline" size="sm" onClick={doLock} data-testid="console-lock-button"><Lock className="mr-1.5 h-4 w-4" />Lock</Button>
        </DTitle>
        {!me.vendor ? <EmptyState title="Seller profile required" text="Create a seller storefront from your dashboard before accepting payments." /> : (
          <Tabs defaultValue="links">
            <TabsList><TabsTrigger value="links" data-testid="devctl-tab-links">Payment links</TabsTrigger><TabsTrigger value="keys" data-testid="devctl-tab-keys">API keys</TabsTrigger>
              <TabsTrigger value="docs" data-testid="devctl-tab-docs">Docs</TabsTrigger><TabsTrigger value="payments" data-testid="devctl-tab-payments">API payments</TabsTrigger></TabsList>
            <TabsContent value="links" className="mt-6"><PaymentLinks /></TabsContent>
            <TabsContent value="keys" className="mt-6"><Keys /></TabsContent>
            <TabsContent value="docs" className="mt-6"><Docs /></TabsContent>
            <TabsContent value="payments" className="mt-6"><ApiPayments /></TabsContent>
          </Tabs>)}
      </div>
    </ConsoleCtx.Provider>
  );
}

export default function DeveloperControl() {
  return (
    <>
      <Show when="signed-out"><Shell><SignIn routing="hash" forceRedirectUrl={window.location.pathname} signUpUrl="/sign-up" /></Shell></Show>
      <Show when="signed-in"><Gate /></Show>
    </>
  );
}
